import { useCallback, useEffect, useState } from 'react';

import { CODE_BOOLEAN, useLocalStorageState, useMediaQuery } from '@datanomik/commons-ui';

import { USER_STATUS } from '@/assets/constants/User';

import { useAppContext } from '@/contexts/app';
import { useUserContext } from '@/contexts/user';
import { useWorkspaceContext } from '@/contexts/workspace';
import { useKeycloakContext } from '@/contexts/keycloak/context';

import { getSubscriptions } from '@/services/Subscription';

const useDashboard = () => {
  const { handleAlert } = useAppContext();
  const {
    userSession,
    setUserSession,
  } = useUserContext();

  const { logoutKeycloak } = useKeycloakContext();

  const {
    workspace: { defaultWorkspace, isOnboarding },
  } = useWorkspaceContext();

  const [open, setOpen] = useState(false);
  const [anchorElClient, setAnchorElClient] = useState(null);
  const [anchorElClientMobile, setAnchorElClientMobile] = useState(null);
  const [anchorElWorkspace, setAnchorElWorkspace] = useState(null);
  const [anchorElWorkspaceMobile, setAnchorElWorkspaceMobile] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorageState(
    'sidebar-collapsed',
    true,
    {
      codec: CODE_BOOLEAN,
    },
  );

  useEffect(() => {
    const fn = async () => {
      try {
        const subscriptions = await getSubscriptions();

        setUserSession({
          ...userSession,
          subscriptions,
        });
      } catch (error) {
        handleAlert(error, 'error');
      }
    };

    if (userSession.status !== USER_STATUS.PENDING && defaultWorkspace.id) fn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession?.clientId, defaultWorkspace.id]);

  const toggleSidebarCollapsed = useCallback(
    () => () => {
      setSidebarCollapsed((prevValue) => !prevValue);
    },
    [setSidebarCollapsed],
  );

  const toggleDrawer = useCallback(
    (newOpen) => () => {
      setOpen(() => newOpen);
    },
    [],
  );

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleClickClient = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorElClient(e.currentTarget);
  };

  const handleClickClientMobile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorElClientMobile(e.currentTarget);
  };

  const handleClickWorkspace = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorElWorkspace(e.currentTarget);
  };

  const handleClickWorkspaceMobile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorElWorkspaceMobile(e.currentTarget);
  };

  const handleCloseMenu = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setAnchorElClient(null);
    setAnchorElClientMobile(null);
    setAnchorElWorkspace(null);
    setAnchorElWorkspaceMobile(null);
  };

  const handleLogoutDashboard = () => {
    logoutKeycloak();
  };

  return {
    open,
    anchorElClient,
    anchorElClientMobile,
    anchorElWorkspace,
    anchorElWorkspaceMobile,
    handleClickClient,
    handleClickClientMobile,
    handleClickWorkspace,
    handleClickWorkspaceMobile,
    handleCloseMenu,
    defaultWorkspace,
    user: userSession,
    isMobile,
    isOnboarding,
    sidebarCollapsed,
    toggleDrawer,
    handleLogout: handleLogoutDashboard,
    toggleSidebarCollapsed,
  };
};

export default useDashboard;
